import { useMutation } from "@tanstack/react-query"
import { ImagePlus, X } from "lucide-react"
import { useState } from "react"

import { ErrorApi, pedir } from "@/api/cliente"

/**
 * Escribir un comentario, con capturas pegadas.
 *
 * ⚠ **Ctrl+V es la forma en que esto se usa de verdad.** Quien comenta una tarea
 * acaba de mirar una pantalla —el error del portal, la planilla, el correo del
 * cliente— y describirla con palabras cuesta un párrafo que además se entiende
 * peor. El portapapeles entrega un blob sin nombre, así que se le pone uno antes de
 * mandarlo.
 *
 * ⚠ **Primero el comentario, después el adjunto.** Son dos peticiones y ese orden
 * importa: el adjunto cuelga del comentario, y al revés habría que inventar un
 * comentario vacío al que pegarlo — que es como acaban las tareas con tres capturas
 * sin ningún texto que explique de qué son.
 *
 * ⚠ **La subida NO pasa por `api.post`.** Va como `multipart/form-data`, y ese
 * módulo pone `Content-Type: application/json` en todo lo que no sea `FormData`. Se
 * usa `pedir()` directamente, igual que el panel de documentos.
 */

type Props = {
  tareaId: number
  onPublicado: () => void
}

export function ComentarioNuevo({ tareaId, onPublicado }: Props) {
  const [cuerpo, setCuerpo] = useState("")
  // Cada captura lleva identidad propia. Con el índice como clave, pegar dos veces
  // la misma imagen —que es lo normal al comparar dos pantallas— haría que React
  // reutilizara el nodo equivocado al quitar una de las dos.
  const [capturas, setCapturas] = useState<{ id: string; archivo: File }[]>([])

  const publicar = useMutation({
    mutationFn: async () => {
      const comentario = await pedir<{ id: number }>(`/api/v1/tareas/${tareaId}/comentarios`, {
        method: "POST",
        body: JSON.stringify({ cuerpo: cuerpo.trim() }),
      })
      for (const captura of capturas) {
        const formulario = new FormData()
        formulario.append("archivo", captura.archivo)
        await pedir(`/api/v1/tareas/${tareaId}/comentarios/${comentario.id}/adjuntos`, {
          method: "POST",
          body: formulario,
        })
      }
    },
    onSuccess: () => {
      setCuerpo("")
      setCapturas([])
      onPublicado()
    },
  })

  /** Saca del portapapeles lo que sean archivos y los guarda con un nombre. */
  const pegar = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const archivos = [...e.clipboardData.items]
      .filter((i) => i.kind === "file")
      .map((i) => i.getAsFile())
      .filter((f): f is File => f !== null)
    if (archivos.length === 0) return
    // Se evita el pegado por defecto sólo cuando SÍ había un archivo: si no, pegar
    // texto junto a una imagen perdería el texto.
    e.preventDefault()
    setCapturas((previas) => [
      ...previas,
      // El portapapeles da `image.png` o cadena vacía. Un nombre propio hace que la
      // lista de adjuntos se pueda leer cuando hay tres.
      ...archivos.map((f, i) => ({
        id: crypto.randomUUID(),
        archivo: new File([f], f.name || `captura-${previas.length + i + 1}.png`, {
          type: f.type,
        }),
      })),
    ])
  }

  const listo = cuerpo.trim() !== ""
  const borde = { borderColor: "var(--border)" }

  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (listo) publicar.mutate()
      }}
    >
      <label className="block text-sm" htmlFor={`comentario-${tareaId}`}>
        Añadir un comentario
        <textarea
          id={`comentario-${tareaId}`}
          rows={2}
          className="campo mt-1"
          value={cuerpo}
          onChange={(e) => setCuerpo(e.target.value)}
          onPaste={pegar}
        />
      </label>
      <p className="mt-1 flex items-center gap-1 text-xs" style={{ color: "var(--fg-muted)" }}>
        <ImagePlus size={12} aria-hidden={true} />
        Pega una captura con Ctrl+V para adjuntarla.
      </p>

      {capturas.length > 0 && (
        <ul className="mt-2 space-y-1">
          {capturas.map((c, i) => (
            <li
              key={c.id}
              data-testid={`captura-${i}`}
              className="flex items-center justify-between gap-2 rounded-[var(--radius-control)] border px-2 py-1 text-xs"
              style={borde}
            >
              <span className="truncate">{c.archivo.name}</span>
              <button
                type="button"
                aria-label={`Quitar ${c.archivo.name}`}
                onClick={() => setCapturas((previas) => previas.filter((p) => p.id !== c.id))}
              >
                <X size={12} aria-hidden={true} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex justify-end">
        <button
          type="submit"
          disabled={!listo || publicar.isPending}
          className="rounded-[var(--radius-control)] px-3 py-1.5 text-sm disabled:opacity-50"
          style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
        >
          {publicar.isPending ? "Enviando…" : "Comentar"}
        </button>
      </div>

      {publicar.isError && (
        <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
          {publicar.error instanceof ErrorApi
            ? publicar.error.message
            : "No se pudo publicar el comentario."}
        </p>
      )}
    </form>
  )
}
